<template>
  <div class="comment-monitor">
    <div class="page-head">
      <div class="head-actions">
        <el-button :icon="Refresh" :loading="polling" :disabled="!selectedMonitorId" @click="pollNow">
          立即抓取
        </el-button>
        <el-button type="warning" plain :icon="Bell" @click="keywordDialogVisible = true">
          预警关键词
        </el-button>
        <el-button type="primary" :icon="Plus" @click="monitorDialogVisible = true">
          添加监控笔记
        </el-button>
      </div>
    </div>

    <el-alert
      v-if="pollErrors.length"
      title="部分监控抓取失败，请检查对应账号的小红书 PC 登录态与 xsec_token"
      type="warning"
      show-icon
      :closable="false"
      class="mb"
    />

    <div class="monitor-list">
      <el-card
        v-for="m in monitors"
        :key="m.id"
        shadow="never"
        :class="['monitor-card', { active: m.id === selectedMonitorId }]"
        @click="selectMonitor(m)"
      >
        <div class="monitor-main">
          <el-image v-if="m.cover_url" :src="proxyCover(m.cover_url)" fit="cover" class="monitor-cover" referrerpolicy="no-referrer" />
          <div v-else class="monitor-cover placeholder">无图</div>
          <div class="monitor-info">
            <div class="monitor-title" :title="m.title">{{ m.title || m.note_id }}</div>
            <div class="monitor-meta">
              <el-tag size="small" :type="m.enabled ? 'success' : 'info'">{{ m.enabled ? '监控中' : '已停用' }}</el-tag>
              <span v-if="m.new_count" class="new-badge">{{ m.new_count }} 新</span>
              <span>共 {{ m.total_count }}</span>
            </div>
            <div class="monitor-note" :title="m.note_url">{{ m.note_id }}</div>
          </div>
        </div>
        <div class="monitor-foot">
          <span>间隔 {{ m.poll_interval_sec }}s</span>
          <span :class="statusClass(m.last_poll_status)">{{ statusText(m.last_poll_status) }}</span>
          <el-button link type="danger" size="small" @click.stop="removeMonitor(m)">移除</el-button>
        </div>
      </el-card>
      <el-empty v-if="!monitors.length" description="还没有监控的笔记，点击右上角添加" />
    </div>

    <el-card shadow="never" class="feed-card">
      <template #header>
        <div class="feed-head">
          <div class="feed-title">
            评论流
            <span class="feed-count" v-if="feedTotal">共 {{ feedTotal }} 条</span>
            <el-tag v-if="onlyNew" type="success" size="small">只看新增</el-tag>
            <el-tag v-if="onlyHighlighted" type="warning" size="small">只看预警</el-tag>
          </div>
          <div class="feed-actions">
            <el-input v-model="search" placeholder="搜索内容或用户" clearable style="width: 200px" @keyup.enter="loadFeed" @clear="loadFeed" />
            <el-select v-model="onlyNew" style="width: 120px" @change="loadFeed">
              <el-option :value="false" label="全部评论" />
              <el-option :value="true" label="只看新增" />
            </el-select>
            <el-select v-model="onlyHighlighted" style="width: 120px" @change="loadFeed">
              <el-option :value="false" label="全部" />
              <el-option :value="true" label="只看预警" />
            </el-select>
            <el-button @click="markAllRead">全部已读</el-button>
          </div>
        </div>
      </template>

      <div v-loading="feedLoading">
        <el-empty v-if="!feedLoading && !feed.length" description="暂无评论" />
        <div v-else class="comment-list">
          <div v-for="c in feed" :key="c.id" class="comment-item" :class="{ highlight: c.is_highlighted, new: c.is_new }">
            <div class="comment-top">
              <strong>{{ c.user_name || '匿名' }}</strong>
              <span class="comment-meta">
                <template v-if="c.ip_location">{{ c.ip_location }} · </template>
                赞 {{ c.like_count }} · {{ fmtTime(c.created_at_remote) }}
              </span>
              <div class="comment-ops">
                <el-tag v-if="c.is_highlighted" type="warning" size="small" class="keyword-hits">
                  {{ (c.matched_keywords || []).join('、') }}
                </el-tag>
                <el-tag v-if="c.reply_status === 'pending'" type="primary" size="small">待回复</el-tag>
                <el-tag v-else-if="c.reply_status === 'replied'" type="success" size="small">已回复</el-tag>
                <el-button v-if="c.is_new" link type="primary" size="small" @click="markRead(c)">标已读</el-button>
                <el-button link type="primary" size="small" @click="openReply(c)">回复</el-button>
              </div>
            </div>
            <div class="comment-content">{{ c.content }}</div>
            <div v-if="c.parent_comment_id" class="comment-sub">楼中楼回复</div>
          </div>
        </div>

        <div v-if="feedTotal > pageSize" class="pager">
          <el-pagination
            v-model:current-page="page"
            v-model:page-size="pageSize"
            :total="feedTotal"
            layout="total, prev, pager, next"
            @current-change="loadFeed"
          />
        </div>
      </div>
    </el-card>

    <!-- 添加监控 -->
    <el-dialog v-model="monitorDialogVisible" title="添加监控笔记" width="520px">
      <el-form :model="monitorForm" label-width="90px">
        <el-form-item label="账号">
          <el-select v-model="monitorForm.account_id" filterable style="width: 100%">
            <el-option v-for="a in accounts" :key="a.account_id" :value="a.account_id" :label="a.display_name || a.account_id" />
          </el-select>
        </el-form-item>
        <el-form-item label="笔记 ID" required>
          <el-input v-model="monitorForm.note_id" placeholder="如 65abc123456789" />
        </el-form-item>
        <el-form-item label="xsec_token">
          <el-input v-model="monitorForm.xsec_token" placeholder="笔记 URL 中的 xsec_token（建议填写）" />
        </el-form-item>
        <el-form-item label="标题">
          <el-input v-model="monitorForm.title" placeholder="便于识别的名称（可选）" />
        </el-form-item>
        <el-form-item label="抓取间隔">
          <el-input-number v-model="monitorForm.poll_interval_sec" :min="30" :max="86400" :step="30" style="width: 200px" />
          <span class="form-hint">秒，默认 300</span>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="monitorDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="monitorSaving" @click="saveMonitor">确定</el-button>
      </template>
    </el-dialog>

    <!-- 关键词配置 -->
    <el-dialog v-model="keywordDialogVisible" title="预警关键词" width="520px">
      <p class="desc-hint">新增评论命中以下关键词时，会在评论流中高亮（置顶由筛选实现）。</p>
      <el-select v-model="keywords" multiple filterable allow-create default-first-option placeholder="输入关键词后回车" style="width: 100%">
        <el-option v-for="k in keywords" :key="k" :value="k" :label="k" />
      </el-select>
      <template #footer>
        <el-button @click="keywordDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="keywordSaving" @click="saveKeywords">保存</el-button>
      </template>
    </el-dialog>

    <!-- 回复 -->
    <el-dialog v-model="replyDialogVisible" :title="replyTarget ? `回复 ${replyTarget.user_name} 的评论` : '回复评论'" width="520px">
      <p class="reply-quote" v-if="replyTarget">“{{ replyTarget.content }}”</p>
      <el-input v-model="replyContent" type="textarea" :rows="4" maxlength="1000" show-word-limit placeholder="请输入回复内容" />
      <div class="reply-note">回复将通过小红书评论回复接口真实发布，请确认内容合规后再提交。</div>
      <template #footer>
        <el-button @click="replyDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="replySaving" @click="saveReply">提交回复</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Refresh, Bell } from '@element-plus/icons-vue'
import { publishV2Api, xhsCommentApi } from '../api'

const monitors = ref([])
const selectedMonitorId = ref(null)
const accounts = ref([])

const feed = ref([])
const feedTotal = ref(0)
const page = ref(1)
const pageSize = ref(50)
const feedLoading = ref(false)
const onlyNew = ref(false)
const onlyHighlighted = ref(false)
const search = ref('')

const polling = ref(false)
const pollErrors = ref([])

const monitorDialogVisible = ref(false)
const monitorSaving = ref(false)
const monitorForm = ref({ account_id: '', note_id: '', xsec_token: '', title: '', poll_interval_sec: 300 })

const keywordDialogVisible = ref(false)
const keywords = ref([])
const keywordSaving = ref(false)

const replyDialogVisible = ref(false)
const replyTarget = ref(null)
const replyContent = ref('')
const replySaving = ref(false)

let pollTimer = null

function proxyCover(url) {
  return publishV2Api.coverProxyUrl(url)
}
function fmtTime(v) {
  if (!v) return '—'
  const n = Number(v)
  if (!n) return String(v)
  try {
    return new Date(n).toLocaleString('zh-CN')
  } catch {
    return String(v)
  }
}
function statusClass(s) {
  if (s === 'success') return 'ok'
  if (s === 'error') return 'err'
  return 'idle'
}
function statusText(s) {
  if (s === 'success') return '已同步'
  if (s === 'error') return '失败'
  return '未同步'
}

async function loadAccounts() {
  try {
    const res = await publishV2Api.listAccounts({ platform: 'xiaohongshu' })
    accounts.value = res.items || []
  } catch (e) {
    accounts.value = []
  }
}

async function loadMonitors() {
  try {
    const res = await xhsCommentApi.listMonitors()
    monitors.value = res.items || []
    if (!selectedMonitorId.value && monitors.value.length) {
      selectedMonitorId.value = monitors.value[0].id
    }
    await loadKeywords()
  } catch (e) {
    ElMessage.error('加载监控列表失败')
  }
}

function selectMonitor(m) {
  selectedMonitorId.value = m.id
  page.value = 1
  loadFeed()
}

async function loadFeed() {
  feedLoading.value = true
  try {
    const res = await xhsCommentApi.getFeed({
      monitor_id: selectedMonitorId.value || undefined,
      only_new: onlyNew.value || undefined,
      only_highlighted: onlyHighlighted.value || undefined,
      search: search.value.trim() || undefined,
      page: page.value,
      page_size: pageSize.value,
    })
    feed.value = res.items || []
    feedTotal.value = res.total || 0
  } catch (e) {
    ElMessage.error('加载评论失败')
  } finally {
    feedLoading.value = false
  }
}

async function pollNow() {
  polling.value = true
  pollErrors.value = []
  try {
    const res = await xhsCommentApi.poll(selectedMonitorId.value)
    const errs = res.errors || []
    if (errs.length) pollErrors.value = errs
    if (res.inserted !== undefined) {
      ElMessage.success(`抓取完成：新增 ${res.inserted} 条，预警 ${res.highlighted} 条`)
    }
    await loadMonitors()
    await loadFeed()
  } catch (e) {
    ElMessage.error('抓取失败')
  } finally {
    polling.value = false
  }
}

async function saveMonitor() {
  if (!monitorForm.value.account_id) { ElMessage.warning('请选择账号'); return }
  if (!monitorForm.value.note_id.trim()) { ElMessage.warning('请填写笔记 ID'); return }
  monitorSaving.value = true
  try {
    await xhsCommentApi.addMonitor(monitorForm.value)
    ElMessage.success('已添加监控')
    monitorDialogVisible.value = false
    monitorForm.value = { account_id: '', note_id: '', xsec_token: '', title: '', poll_interval_sec: 300 }
    await loadMonitors()
  } catch (e) {
    ElMessage.error(e?.response?.data?.detail || '添加失败')
  } finally {
    monitorSaving.value = false
  }
}

async function removeMonitor(m) {
  try {
    await ElMessageBox.confirm(`移除监控“${m.title || m.note_id}”？将同时清空其评论记录`, '确认', { type: 'warning' })
  } catch {
    return
  }
  try {
    await xhsCommentApi.deleteMonitor(m.id)
    if (selectedMonitorId.value === m.id) selectedMonitorId.value = null
    await loadMonitors()
    await loadFeed()
  } catch (e) {
    ElMessage.error('移除失败')
  }
}

async function loadKeywords() {
  try {
    const res = await xhsCommentApi.getKeywords()
    keywords.value = res.keywords || []
  } catch (e) {
    keywords.value = []
  }
}
async function saveKeywords() {
  keywordSaving.value = true
  try {
    const res = await xhsCommentApi.putKeywords(keywords.value)
    keywords.value = res.keywords || []
    ElMessage.success('关键词已保存')
    keywordDialogVisible.value = false
  } catch (e) {
    ElMessage.error('保存失败')
  } finally {
    keywordSaving.value = false
  }
}

async function markRead(c) {
  try {
    await xhsCommentApi.markRead(c.id)
    c.is_new = false
  } catch (e) { /* ignore */ }
}
async function markAllRead() {
  await xhsCommentApi.markAllRead(selectedMonitorId.value)
  await loadFeed()
}

function openReply(c) {
  replyTarget.value = c
  replyContent.value = ''
  replyDialogVisible.value = true
}
async function saveReply() {
  if (!replyContent.value.trim()) { ElMessage.warning('请输入回复内容'); return }
  replySaving.value = true
  try {
    await xhsCommentApi.reply(replyTarget.value.id, replyContent.value.trim())
    ElMessage.success('回复已发布到小红书')
    replyDialogVisible.value = false
    await loadFeed()
  } catch (e) {
    ElMessage.error(e?.response?.data?.detail || '回复失败')
  } finally {
    replySaving.value = false
  }
}

onMounted(async () => {
  await Promise.all([loadAccounts(), loadMonitors()])
  if (selectedMonitorId.value) await loadFeed()
  pollTimer = setInterval(() => {
    if (selectedMonitorId.value) {
      xhsCommentApi.poll(selectedMonitorId.value).then(() => {
        loadFeed()
      }).catch(() => {})
    }
  }, 60000)
})
</script>

<style scoped>
.page-head { display: flex; justify-content: flex-end; align-items: flex-start; margin-bottom: 16px; }
.page-title { margin: 0 0 4px; font-size: 20px; }
.page-desc { margin: 0; color: #8a94a6; font-size: 13px; }
.head-actions { display: flex; gap: 8px; }
.mb { margin-bottom: 12px; }
.monitor-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 12px; margin-bottom: 16px; }
.monitor-card { cursor: pointer; transition: border-color .2s; }
.monitor-card.active { border-color: var(--el-color-primary); }
.monitor-main { display: flex; gap: 12px; align-items: flex-start; }
.monitor-cover { width: 64px; height: 64px; border-radius: 6px; }
.monitor-cover.placeholder { display: flex; align-items: center; justify-content: center; background: #f0f2f5; color: #a2adbe; font-size: 12px; }
.monitor-info { min-width: 0; flex: 1; }
.monitor-title { font-weight: 600; margin-bottom: 6px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.monitor-meta { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; font-size: 12px; color: #8a94a6; }
.new-badge { color: #e6a23c; font-weight: 600; }
.monitor-note { font-size: 12px; color: #a2adbe; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.monitor-foot { display: flex; justify-content: space-between; align-items: center; margin-top: 10px; font-size: 12px; color: #8a94a6; }
.monitor-foot .ok { color: #67c23a; }
.monitor-foot .err { color: #f56c6c; }
.monitor-foot .idle { color: #a2adbe; }
.feed-head { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px; }
.feed-title { display: flex; align-items: center; gap: 8px; font-weight: 600; }
.feed-count { color: #8a94a6; font-weight: 400; }
.feed-actions { display: flex; align-items: center; gap: 8px; }
.comment-list { max-height: 600px; overflow: auto; }
.comment-item { padding: 12px; border-bottom: 1px solid #f0f2f5; border-radius: 4px; }
.comment-item.highlight { background: #fdf6ec; }
.comment-item.new { border-left: 3px solid var(--el-color-primary); }
.comment-top { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 6px; }
.comment-meta { color: #a2adbe; font-size: 12px; }
.comment-ops { display: flex; align-items: center; gap: 6px; }
.keyword-hits { margin-right: 0; }
.comment-content { margin-top: 6px; color: #333; word-break: break-word; }
.comment-sub { margin-top: 4px; font-size: 12px; color: #8a94a6; }
.pager { margin-top: 12px; display: flex; justify-content: flex-end; }
.form-hint { margin-left: 8px; color: #a2adbe; font-size: 12px; }
.desc-hint { color: #8a94a6; font-size: 13px; margin: 0 0 12px; }
.reply-quote { background: #f5f7fa; padding: 8px 12px; border-radius: 4px; color: #606266; margin-bottom: 12px; font-size: 13px; }
.reply-note { color: #e6a23c; font-size: 12px; margin-top: 8px; }
</style>
